import { AnimatePresence, motion } from "framer-motion";
import {
  FolderPlus,
  LayoutGrid,
  List,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  SearchX,
  Trash2,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { api } from "../../lib/api";
import { handleError } from "../../lib/errors";
import { fadeInUp, stagger } from "../../lib/motion";
import { notify } from "../../lib/notify";
import { StatusTone } from "../../lib/statusMeta";
import { cn, formatRelativeTime } from "../../lib/utils";
import { useAppStore } from "../../store/useAppStore";
import { Project, Service } from "../../types";
import { EditProjectDialog } from "../projects/EditProjectDialog";
import { NewProjectDialog } from "../projects/NewProjectDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { EmptyState } from "../ui/empty-state";
import { ErrorState } from "../ui/error-state";
import { Input } from "../ui/input";
import { PageHeader } from "../ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { SkeletonCard, SkeletonRow } from "../ui/skeleton";
import { StatusDot } from "../ui/status-dot";

type SortKey = "updated" | "name" | "services";

const SORT_STORAGE_KEY = "avahana:projects-sort";
const VIEW_MODE_KEY = "avahana:projects-view";
type ViewMode = "grid" | "list";

// Deterministic avatar color based on project id.
const AVATAR_COLORS = [
  "bg-rose-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500",
  "bg-teal-500", "bg-cyan-500", "bg-blue-500", "bg-violet-500",
  "bg-purple-500", "bg-pink-500", "bg-indigo-500", "bg-sky-500",
];

function projectColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash * 31) + id.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// Every new project gets these three environments, so showing them as badges
// on each card is pure noise — only surface envs when they deviate.
const DEFAULT_ENVS = ["development", "staging", "production"];

function hasCustomEnvs(environments: string[]): boolean {
  return (
    environments.length > 0 &&
    (environments.length !== DEFAULT_ENVS.length ||
      !DEFAULT_ENVS.every((env) => environments.includes(env)))
  );
}

// Maps a derived health tone to its semantic text color.
const HEALTH_TEXT: Record<StatusTone, string> = {
  positive: "text-success",
  warning: "text-warning",
  negative: "text-destructive",
  neutral: "text-muted-foreground",
  info: "text-info",
};

interface ProjectHealth {
  total: number;
  tone: StatusTone;
  summary: string;
}

// Collapses a project's services into a single at-a-glance health signal so the
// card can answer "can I trust this?" rather than just "how many services?".
// Priority: any failure (negative) > anything building (warning) > all running
// (positive) > otherwise neutral.
function getProjectHealth(services: Service[] = []): ProjectHealth {
  const total = services.length;
  if (total === 0)
    return { total, tone: "neutral", summary: "No services yet" };

  const running = services.filter((s) => s.status === "running").length;
  const failed = services.filter((s) => s.status === "failed").length;
  const building = services.filter(
    (s) => s.status === "building" || s.status === "pending"
  ).length;

  if (failed > 0)
    return { total, tone: "negative", summary: `${failed} failed` };
  if (building > 0)
    return { total, tone: "warning", summary: `${building} deploying` };
  if (running === total)
    return { total, tone: "positive", summary: "All running" };
  if (running > 0)
    return { total, tone: "positive", summary: `${running}/${total} running` };
  return {
    total,
    tone: "neutral",
    summary: `${total} service${total > 1 ? "s" : ""}`,
  };
}

interface ProjectRowProps {
  project: Project;
  onEdit: () => void;
  onDelete: () => void;
}

function ProjectRow({ project, onEdit, onDelete }: ProjectRowProps) {
  const health = getProjectHealth(project.services);
  const environments = project.environments ?? [];
  return (
    <motion.div
      variants={fadeInUp}
      className="group relative flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40 sm:gap-4"
    >
      <Link
        to={`/projects/${project.id}`}
        className="absolute inset-0 z-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        aria-label={`Open ${project.name}`}
      />

      {/* Avatar + name — always visible */}
      <div className="pointer-events-none flex min-w-0 flex-1 items-center gap-3">
        <div
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white",
            projectColor(project.id)
          )}
          aria-hidden="true"
        >
          {project.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium">{project.name}</p>
            {/* "Shared" badge — visible when the caller is not the owner, meaning
                this project was shared with them through the members feature. */}
            {project.role && project.role !== "owner" && (
              <Badge variant="outline" className="text-2xs font-normal shrink-0">
                Shared
              </Badge>
            )}
          </div>
          {/* Description — shown below name on mobile only */}
          {project.description && (
            <p className="truncate text-xs text-muted-foreground sm:hidden">
              {project.description}
            </p>
          )}
        </div>
      </div>

      {/* Description column — sm+ */}
      <div className="pointer-events-none hidden w-48 shrink-0 sm:block lg:w-60">
        {project.description ? (
          <p className="truncate text-sm text-muted-foreground">
            {project.description}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground/40" aria-hidden="true">
            —
          </p>
        )}
        {/* "Owned by" line for shared projects — ownerName comes from the backend
            and is only populated when the caller is not the project creator. */}
        {project.role && project.role !== "owner" && project.ownerName && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground/70">
            Owned by {project.ownerName}
          </p>
        )}
      </div>

      {/* Env badges column — lg+ (always rendered for column alignment; badges
          only appear when the project deviates from the default env trio) */}
      <div className="pointer-events-none hidden w-36 shrink-0 items-center gap-1 lg:flex">
        {hasCustomEnvs(environments) &&
        environments.slice(0, 2).map((env) => (
          <Badge key={env} variant="secondary" className="text-2xs font-normal">
            {env}
          </Badge>
        ))}
        {hasCustomEnvs(environments) && environments.length > 2 && (
          <Badge
            variant="secondary"
            className="text-2xs font-normal"
            title={environments.slice(2).join(", ")}
          >
            +{environments.length - 2}
          </Badge>
        )}
      </div>

      {/* Health column — sm+ */}
      <div className="pointer-events-none hidden w-36 shrink-0 items-center gap-1.5 text-xs sm:flex">
        <StatusDot tone={health.tone} pulse={health.tone === "warning"} size="sm" />
        <span className={cn("font-medium", HEALTH_TEXT[health.tone])}>
          {health.summary}
        </span>
        {health.total > 0 && (
          <span className="text-muted-foreground">· {health.total}</span>
        )}
      </div>

      {/* Health dot only — mobile */}
      <div className="pointer-events-none shrink-0 sm:hidden">
        <StatusDot tone={health.tone} pulse={health.tone === "warning"} size="sm" />
      </div>

      {/* Updated column — lg+ */}
      <span
        className="pointer-events-none hidden w-20 shrink-0 text-right text-xs text-muted-foreground lg:block"
        title={project.updatedAt ? new Date(project.updatedAt).toLocaleString() : undefined}
      >
        {project.updatedAt ? formatRelativeTime(project.updatedAt) : "—"}
      </span>

      {/* Actions — always visible in list mode */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="pointer-events-auto relative z-10 h-8 w-8 shrink-0 text-muted-foreground opacity-40 transition-opacity hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={`Actions for ${project.name}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </motion.div>
  );
}

export function ProjectsView() {
  useDocumentTitle("Projects");
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const { projects, setProjects } = useAppStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  // Toolbar state — persisted in the URL so back-button and sharing work.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const sort: SortKey =
    (searchParams.get("sort") as SortKey) ??
    (localStorage.getItem(SORT_STORAGE_KEY) as SortKey) ??
    "updated";

  function updateSearch(params: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(params)) {
        if (v == null || v === "") next.delete(k);
        else next.set(k, v);
      }
      return next;
    });
  }

  // Delete-confirmation state (single dialog driven by the pending project, so we
  // don't nest an AlertDialog inside each clickable card).
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Project being edited (drives a single shared EditProjectDialog).
  const [pendingEdit, setPendingEdit] = useState<Project | null>(null);

  // View mode — persisted across sessions.
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) ?? "grid"
  );
  function setAndSaveViewMode(mode: ViewMode) {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
  }

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.projects.list();
      setProjects(data.projects);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setProjects]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Filter by name/description, then sort. Memoized so typing stays cheap.
  const visibleProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? projects.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q)
        )
      : projects;

    const sorted = [...filtered].sort((a, b) => {
      switch (sort) {
        case "name":
          return a.name.localeCompare(b.name);
        case "services":
          return (b.services?.length ?? 0) - (a.services?.length ?? 0);
        case "updated":
        default:
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
      }
    });
    return sorted;
  }, [projects, query, sort]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setDeleting(true);
    try {
      await api.projects.delete(target.id);
      notify.success("Project deleted", { description: target.name });
      setPendingDelete(null);
      await fetchProjects();
    } catch (err) {
      handleError(err, { scope: "ProjectsView", context: { id: target.id } });
    } finally {
      setDeleting(false);
    }
  };

  const hasProjects = projects.length > 0;

  return (
    <div className="space-y-6">
      {/* Header: title + live count, and the create action */}
      <PageHeader
        eyebrow="Workspace"
        title="Projects"
        description="Manage your applications and deployments"
        count={hasProjects ? projects.length : undefined}
        actions={
        <NewProjectDialog
          open={showNewProjectDialog}
          onOpenChange={setShowNewProjectDialog}
          onProjectCreated={() => {
            setShowNewProjectDialog(false);
            notify.success("Project created");
            fetchProjects();
          }}
        />
        }
      />

      {/* Search + sort toolbar — only meaningful once projects exist */}
      {hasProjects && !error && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={(e) => updateSearch({ q: e.target.value })}
              placeholder="Search projects…"
              className={cn("pl-9", query && "pr-8")}
              aria-label="Search projects"
            />
            {query && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => updateSearch({ q: null })}
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          {query && (
            <span className="shrink-0 text-sm text-muted-foreground">
              {visibleProjects.length} of {projects.length}
            </span>
          )}
          <Select
            value={sort}
            onValueChange={(v) => {
              localStorage.setItem(SORT_STORAGE_KEY, v);
              updateSearch({ sort: v });
            }}
          >
            <SelectTrigger className="sm:w-52" aria-label="Sort projects">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated">Recently updated</SelectItem>
              <SelectItem value="name">Name (A–Z)</SelectItem>
              <SelectItem value="services">Most services</SelectItem>
            </SelectContent>
          </Select>

          {/* View mode toggle */}
          <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-card p-0.5">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="icon"
              className="h-7 w-7"
              onClick={() => setAndSaveViewMode("grid")}
              aria-label="Grid view"
              aria-pressed={viewMode === "grid"}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="icon"
              className="h-7 w-7"
              onClick={() => setAndSaveViewMode("list")}
              aria-label="List view"
              aria-pressed={viewMode === "list"}
            >
              <List className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        // Skeletons match the active view so there's no layout shift on load.
        viewMode === "list" ? (
          <div className="overflow-hidden rounded-lg bg-card divide-y">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )
      ) : error ? (
        <ErrorState
          title="Couldn't load your projects"
          error={error}
          onRetry={fetchProjects}
        />
      ) : projects.length === 0 ? (
        <EmptyState
          icon={FolderPlus}
          title="No projects yet"
          description="Create your first project to start deploying services to Avahana."
          primaryAction={{
            label: "Create your first project",
            icon: Plus,
            onClick: () => setShowNewProjectDialog(true),
          }}
        />
      ) : visibleProjects.length === 0 ? (
        // Projects exist but the search matched nothing — distinct from "no projects".
        <EmptyState
          icon={SearchX}
          title="No matching projects"
          description={`Nothing matches “${query}”. Try a different search.`}
          primaryAction={{ label: "Clear search", onClick: () => updateSearch({ q: null }) }}
        />
      ) : (
        <>
          <AnimatePresence mode="wait" initial={false}>
          {viewMode === "list" ? (
            <motion.div
              key="list"
              className="overflow-hidden rounded-lg bg-card divide-y"
              variants={stagger}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, transition: { duration: 0.12 } }}
            >
              {visibleProjects.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  onEdit={() => setPendingEdit(project)}
                  onDelete={() => setPendingDelete(project)}
                />
              ))}
            </motion.div>
          ) : (
          <motion.div
            key="grid"
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            variants={stagger}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, transition: { duration: 0.12 } }}
          >
            {visibleProjects.map((project) => {
              const health = getProjectHealth(project.services);
              const environments = project.environments ?? [];
              return (
                <motion.div key={project.id} variants={fadeInUp}>
                <Card
                  className="group relative overflow-hidden transition-all hover:border-primary/50"
                >
                  {/* Full-coverage router link — gives native right-click, keyboard focus,
                      and screen-reader "link" semantics without a div+role anti-pattern. */}
                  <Link
                    to={`/projects/${project.id}`}
                    className="absolute inset-0 z-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                    aria-label={`Open ${project.name}`}
                  />

                  <CardHeader className="pointer-events-none pb-3">
                    <CardTitle className="flex items-center justify-between gap-2 text-base">
                      <div className="flex min-w-0 items-center gap-2">
                        {/* Deterministic avatar — makes projects scannable without reading names */}
                        <div
                          className={cn(
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white",
                            projectColor(project.id)
                          )}
                          aria-hidden="true"
                        >
                          {project.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{project.name}</span>
                          {/* Shared badge — only shown on cards where caller is not the owner */}
                          {project.role && project.role !== "owner" && (
                            <Badge variant="outline" className="mt-0.5 w-fit text-2xs font-normal">
                              Shared
                            </Badge>
                          )}
                        </div>
                      </div>
                      {/* Dropdown trigger must be pointer-events-auto + z-10 to sit above
                          the block link. No stopPropagation needed — the card has no onClick. */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="pointer-events-auto relative z-10 -mr-2 h-8 w-8 shrink-0 text-muted-foreground opacity-40 transition-opacity hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
                            aria-label={`Actions for ${project.name}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setPendingEdit(project)}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setPendingDelete(project)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="pointer-events-none space-y-3">
                    {project.description ? (
                      <p className="line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground">
                        {project.description}
                      </p>
                    ) : (
                      /* Empty spacer keeps card footers aligned across the row
                         without an italic "No description" placeholder. */
                      <div className="min-h-[2.5rem]" aria-hidden="true" />
                    )}

                    {/* "Owned by" line — only rendered on shared projects where
                        the backend has populated ownerName on the project response. */}
                    {project.role && project.role !== "owner" && project.ownerName && (
                      <p className="text-xs text-muted-foreground">
                        Owned by {project.ownerName}
                      </p>
                    )}

                    {hasCustomEnvs(environments) && (
                      <div className="flex flex-wrap gap-1">
                        {environments.slice(0, 3).map((env) => (
                          <Badge
                            key={env}
                            variant="secondary"
                            className="text-2xs font-normal"
                          >
                            {env}
                          </Badge>
                        ))}
                        {environments.length > 3 && (
                          <Badge
                            variant="secondary"
                            className="text-2xs font-normal"
                            title={environments.slice(3).join(", ")}
                          >
                            +{environments.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Footer: health dot + summary + service count + last-updated */}
                    <div className="flex items-center justify-between pt-1 text-xs">
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot
                          tone={health.tone}
                          pulse={health.tone === "warning"}
                          size="sm"
                        />
                        <span
                          className={cn("font-medium", HEALTH_TEXT[health.tone])}
                        >
                          {health.summary}
                        </span>
                        {health.total > 0 && (
                          <span className="text-muted-foreground">
                            · {health.total}
                          </span>
                        )}
                      </span>
                      {project.updatedAt && (
                        <span
                          className="text-muted-foreground"
                          title={new Date(project.updatedAt).toLocaleString()}
                        >
                          {formatRelativeTime(project.updatedAt)}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
                </motion.div>
              );
            })}
          </motion.div>
          )}
          </AnimatePresence>
        </>
      )}

      {/* Shared edit dialog, mounted only while a project is being edited. */}
      {pendingEdit && (
        <EditProjectDialog
          project={pendingEdit}
          open
          onOpenChange={(o) => {
            if (!o) setPendingEdit(null);
          }}
          onProjectUpdated={fetchProjects}
        />
      )}

      {/* Single delete-confirmation dialog, driven by the pending project. */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the project and all of its services and
              deployment history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                // Keep the dialog mounted while the async delete runs; we close it
                // ourselves on success so the spinner state is visible.
                e.preventDefault();
                confirmDelete();
              }}
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
